import api, { request } from '../../../api';
import { IErrorsStore } from '../../errors/store';
import { IActionGroup, IStore } from '../../index';
import { IAdminRoundFormatsStore, IFormat, IMetaFormat, IMetaFormatBluePrint } from './store';

export interface IAdminRoundFormatsActions extends IActionGroup {
  create: (
    clubId: number,
    eventId: number,
    roundId: number,
    data: Partial<IMetaFormat>
  ) => { adminRoundFormats?: IAdminRoundFormatsStore; errors?: IErrorsStore };
  createRoundFormat: (
    clubId: number,
    eventId: number,
    data: Partial<IFormat>
  ) => { adminRoundFormats?: IAdminRoundFormatsStore; errors?: IErrorsStore };
  delete: (
    clubId: number,
    eventId: number,
    roundId: number,
    roundFormatId: number
  ) => { adminRoundFormats?: IAdminRoundFormatsStore; errors?: IErrorsStore };
  deleteRoundFormat: (
    clubId: number,
    eventId: number,
    roundId: number,
    roundFormatId: number
  ) => { adminRoundFormats?: IAdminRoundFormatsStore; errors?: IErrorsStore };
  get: (
    clubId: number,
    eventId: number,
    roundId: number,
    force?: boolean
  ) => { adminRoundFormats?: IAdminRoundFormatsStore; errors?: IErrorsStore };
  getMasterFormats: () => {
    adminRoundFormats?: IAdminRoundFormatsStore;
    errors?: IErrorsStore;
  };
  getRoundFormats: (
    clubId: number,
    eventId: number,
    force?: boolean
  ) => { adminRoundFormats?: IAdminRoundFormatsStore; errors?: IErrorsStore };
  updateRoundFormat: (
    clubId: number,
    eventId: number,
    formatId: number,
    data: Partial<IMetaFormatBluePrint>
  ) => { adminRoundFormats?: IAdminRoundFormatsStore; errors?: IErrorsStore };
}

const actions: IActionGroup = {
  create: async (
    state: IStore,
    clubId: number,
    eventId: number,
    roundId: number,
    data: Partial<IMetaFormat>
  ) => {
    const response = await request.post(
      api.admin.eventRounds(clubId, eventId, roundId) + 'formats/',
      data
    );

    if (response.error) {
      return { errors: { adminRoundFormats: { msg: 'Error creating round format.' } } };
    }
    const roundFormats = (state.adminRoundFormats[roundId] || []).concat([response]);

    return {
      adminRoundFormats: {
        ...state.adminRoundFormats,
        [roundId]: roundFormats
      }
    };
  },

  createRoundFormat: async (
    state: IStore,
    clubId: number,
    eventId: number,
    data: Partial<IFormat>
  ) => {
    const response = await request.post(api.admin.eventFormats(clubId, eventId), data);

    if (response.error) {
      return { errors: { adminEventFormats: { msg: 'Error creating round format.' } } };
    }

    return {
      adminRoundFormats: {
        ...state.adminRoundFormats,
        roundFormats: {
          ...state.adminRoundFormats.roundFormats,
          [eventId]: state.adminRoundFormats.roundFormats[eventId]
            ? [
                ...state.adminRoundFormats.roundFormats[eventId],
                ...replaceFormatPrefixUnderscore([response])
              ]
            : replaceFormatPrefixUnderscore([response])
        }
      }
    };
  },
  delete: async (
    state: IStore,
    clubId: number,
    eventId: number,
    roundId: number,
    roundFormatId: number
  ) => {
    const response = await request.delete(
      api.admin.eventRounds(clubId, eventId, roundId) + `formats/${roundFormatId}/`
    );

    if (response.error) {
      return { errors: { adminRoundFormats: { msg: 'Error deleting round format.' } } };
    }
    const roundFormats = (state.adminRoundFormats[roundId] || []).filter(
      rf => rf.id !== roundFormatId
    );

    const remainingFormats = state.adminRoundFormats.roundFormats[eventId]
      ? state.adminRoundFormats.roundFormats[eventId].filter(
          (rf: IMetaFormat) => rf.id !== roundFormatId
        )
      : [];

    return {
      adminRoundFormats: {
        ...state.adminRoundFormats,
        [roundId]: roundFormats,
        roundFormats: { ...state.adminRoundFormats.roundFormats, [eventId]: remainingFormats }
      }
    };
  },
  deleteRoundFormat: async (state: IStore, clubId: number, eventId: number, formatId: number) => {
    const response = await request.delete(api.admin.eventFormat(clubId, eventId, formatId));

    if (response.error) {
      return { errors: { adminRoundFormats: { msg: 'Error deleting round format.' } } };
    }

    const remainingFormats = state.adminRoundFormats.roundFormats[eventId].filter(
      roundFormat => roundFormat.id !== formatId
    );

    return {
      adminRoundFormats: {
        ...state.adminRoundFormats,
        roundFormats: { ...state.adminRoundFormats.roundFormats, [eventId]: remainingFormats }
      }
    };
  },
  get: async (state: IStore, clubId: number, eventId: number, roundId: number, force?: boolean) => {
    if (state.adminRoundFormats[roundId] && !force) {
      return {
        adminRoundFormats: state.adminRoundFormats
      };
    }

    const response = await request.get(api.admin.roundFormats(clubId, eventId, roundId));

    if (response.error) {
      return {
        errors: { adminRoundFormats: { msg: 'Error retrieving the Event Round Formats.' } }
      };
    }

    const formats: IMetaFormat[] = response;

    return {
      adminRoundFormats: {
        ...state.adminRoundAwards,
        [roundId]: replaceFormatPrefixUnderscore(formats)
      }
    };
  },
  getMasterFormats: async (state: IStore) => {
    if (state.adminRoundFormats.masterFormats.length) {
      return { adminRoundFormats: state.adminRoundFormats };
    }

    const response = await request.get(api.admin.formats);

    if (response.error) {
      return {
        errors: { adminRoundFormats: { msg: 'Error retrieving the Available Formats.' } }
      };
    }
    const masterFormats: IFormat[] = response;

    return {
      adminRoundFormats: {
        ...state.adminRoundFormats,
        masterFormats
      }
    };
  },
  getRoundFormats: async (state: IStore, clubId: number, eventId: number, force?: boolean) => {
    if (
      !!state.adminRoundFormats.roundFormats[eventId] &&
      !!state.adminRoundFormats.roundFormats[eventId].length &&
      !force
    ) {
      return {
        adminRoundFormats: state.adminRoundFormats
      };
    }

    const response = await request.get(api.admin.allRoundFormats(clubId, eventId));

    if (response.error) {
      return {
        errors: { adminRoundFormats: { msg: 'Error retrieving the Event Round Formats.' } }
      };
    }

    const formats: IMetaFormat[] = response;
    const roundFormats = replaceFormatPrefixUnderscore(formats);

    return {
      adminRoundFormats: {
        ...state.adminRoundFormats,
        roundFormats: { ...state.adminRoundFormats.roundFormats, [eventId]: roundFormats }
      }
    };
  },
  updateRoundFormat: async (
    state: IStore,
    clubId: number,
    eventId: number,
    formatId: number,
    data: IMetaFormatBluePrint
  ) => {
    const response = await request.patch(api.admin.eventFormat(clubId, eventId, formatId), data);

    if (response.error) {
      return { errors: { adminRoundFormats: { msg: 'Error creating round format.' } } };
    }

    const remaininEventFormats = state.adminRoundFormats.roundFormats[eventId].filter(
      roundFormat => roundFormat.id !== formatId
    );

    return {
      adminRoundFormats: {
        ...state.adminRoundFormats,
        roundFormats: {
          ...state.adminRoundFormats.roundFormats,
          [eventId]: [response, ...remaininEventFormats]
        }
      }
    };
  }
};

export default actions;

const replaceFormatPrefixUnderscore = (formats: IMetaFormat[]) =>
  formats.map((roundFormat: IMetaFormat) => {
    return {
      ...roundFormat,
      format: {
        ...roundFormat.format,
        prefix: roundFormat.format.prefix.replace('_', '')
      }
    };
  });
